import { ageEnAnnees, type SchemaDeGarde } from '@repere/core';
import { Link } from '@tanstack/react-router';
import { Avatar } from '../dossier/Avatar';
import { dateComplete, heure } from '../dossier/dates';
import { nomDuDossier } from '../dossier/prenoms';
import type { EnfantDuDossier, ParentDuDossier, SuppressionDossier } from '../dossier/types';
import type { Nouveaute } from '../dossier/Nouveautes';
import { Invisible } from '../ui';

export interface GardeDuJour {
  parentSlot: number | null;
  source: 'schedule' | 'exception' | null;
  until: string | null;
}

export interface DossierEnCarte {
  id: string;
  label: string | null;
  children: EnfantDuDossier[];
  parents: ParentDuDossier[];
  state: { mode: 'solo' | 'shared'; messagingEnabled: boolean; pendingInvitation: boolean };
  today: GardeDuJour;
  nouveautes: Nouveaute[];
  custodyKind: SchemaDeGarde | null;
  deletion: SuppressionDossier | null;
}

/**
 * « 9 ans et 6 ans », ou « 7 ans · né(e) le 14 mars 2019 » quand il n'y en a qu'un.
 *
 * Le calcul vient du noyau : il est déjà celui de l'avatar, et deux soustractions de dates
 * écrites séparément finissent par diverger d'un jour autour des anniversaires.
 */
function ages(enfants: EnfantDuDossier[], maintenant: Date): string {
  const age = (naissance: string): number => ageEnAnnees(new Date(naissance), maintenant);

  if (enfants.length === 1) {
    const enfant = enfants[0] as EnfantDuDossier;
    return `${age(enfant.birthDate)} ans · né(e) le ${dateComplete(new Date(enfant.birthDate))}`;
  }
  return enfants.map((enfant) => `${age(enfant.birthDate)} ans`).join(' et ');
}

/**
 * Un dossier, sur l'écran d'accueil.
 *
 * L'ancienne version énumérait ce qu'elle savait — un titre « Dossier », un prénom, un
 * lien souligné, un paragraphe d'explication — sans répondre à ce qui amène le parent ici :
 * où est mon enfant, et qu'est-ce qui m'attend. Quatre décisions en découlent :
 *
 * 1. **La carte entière est le lien.** Un lien souligné de 130 px au milieu d'une carte de
 *    870 px est une cible minuscule pour l'unique action de l'écran.
 * 2. **Le dossier porte le prénom des enfants**, pas le mot « Dossier ». C'est ainsi qu'un
 *    parent le nomme, et un dossier sans intitulé n'a pas à s'annoncer comme un formulaire.
 * 3. **La garde du jour est écrite en clair, avec son échéance.** « Chez vous » sans date
 *    de fin laisse chercher dans le calendrier ce qu'on venait justement vérifier.
 * 4. **Le pied de carte disparaît quand il n'y a rien de nouveau.** Une carte plus courte
 *    se lit comme « rien à faire ici » — c'est exactement l'information, et « 0 nouveauté »
 *    ne l'aurait pas dite mieux.
 *
 * Ce que la carte ne dit jamais : rien du passage de l'autre parent. Les comptes se
 * calculent sur un repère propre au lecteur, et les libellés viennent du noyau, sans sujet.
 */
export function CarteDossier({
  dossier,
  maintenant,
}: {
  dossier: DossierEnCarte;
  maintenant: Date;
}) {
  const moi = dossier.parents.find((parent) => parent.isViewer);
  const chezMoi = dossier.today.parentSlot !== null && dossier.today.parentSlot === moi?.slot;
  const autre = dossier.parents.find((parent) => !parent.isViewer);
  const fin = dossier.today.until ? new Date(dossier.today.until) : null;
  const titre = nomDuDossier(dossier.label, dossier.children, dossier.custodyKind);
  const sujet =
    dossier.children.length > 1
      ? 'Ils sont'
      : `${dossier.children[0]?.firstName ?? 'L’enfant'} est`;

  // Un dossier archivé est **grisé, et rien de plus** : la carte reste un lien, le dossier
  // reste ouvrable, et tout ce qu'il contient reste lisible jusqu'à la purge. Le retirer de
  // la liste ou le rendre inerte ferait croire à une perte de données là où il n'y a qu'un
  // délai de rétractation qu'on peut encore interrompre.
  const archive = dossier.deletion?.status === 'archived';
  const purge = dossier.deletion?.purgeAt ? new Date(dossier.deletion.purgeAt) : null;

  return (
    <article className={`carte-dossier${archive ? ' archive' : ''}`}>
      <Link
        to="/dossiers/$dossierId"
        params={{ dossierId: dossier.id }}
        className="carte-dossier-lien"
      >
        <div className="carte-dossier-corps">
          {/* Les avatars avant le titre : sur une liste de dossiers, ce sont eux qu'on
              reconnaît avant de lire. L'âge posé dessus évite de le calculer de tête à
              partir d'une date de naissance. */}
          {dossier.children.length > 0 ? (
            <div className="carte-dossier-avatars">
              {dossier.children.map((enfant) => (
                <Avatar
                  key={enfant.id}
                  childId={enfant.id}
                  avatar={enfant.avatar}
                  prenom={enfant.firstName}
                  naissance={enfant.birthDate}
                  maintenant={maintenant}
                />
              ))}
            </div>
          ) : null}

          <div className="carte-dossier-texte">
            <div className="carte-dossier-titre">
              <h2>{titre}</h2>
              {/* Le jeton d'archive prend la place de celui du mode : les deux se
                  disputeraient la même ligne, et savoir qu'un dossier est en cours de
                  suppression prime sur savoir qu'il est partagé. */}
              {archive ? (
                <span className="jeton archive">
                  {purge ? `Archivé · suppression le ${dateComplete(purge)}` : 'Archivé'}
                </span>
              ) : (
                <span className={dossier.state.mode === 'solo' ? 'jeton solo' : 'jeton partage'}>
                  {dossier.state.mode === 'solo'
                    ? 'Vous seul pour l’instant'
                    : `Partagé avec ${autre?.displayName ?? 'l’autre parent'}`}
                </span>
              )}
            </div>

            {dossier.children.length > 0 ? (
              <p className="discret carte-dossier-enfants">{ages(dossier.children, maintenant)}</p>
            ) : null}

            <p className="carte-dossier-garde">
              {dossier.today.parentSlot === null ? (
                <span className="discret">Aucun schéma de garde ne couvre aujourd’hui.</span>
              ) : (
                <>
                  {sujet} <strong>{chezMoi ? 'chez vous' : `chez ${autre?.displayName ?? 'l’autre parent'}`}</strong>
                  {fin ? (
                    <>
                      {' '}jusqu’au {dateComplete(fin)}, {heure(fin)}
                    </>
                  ) : null}
                  {dossier.today.source === 'exception' ? ', par dérogation convenue' : ''}.
                </>
              )}
            </p>
          </div>

          <span className="carte-dossier-chevron" aria-hidden="true">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m9 6 6 6-6 6" />
            </svg>
          </span>
        </div>

        {dossier.nouveautes.length > 0 ? (
          <div className="carte-dossier-nouveautes">
            <ul>
              {dossier.nouveautes.map((nouveaute) => (
                <li key={nouveaute.kind}>
                  <span className="compte" aria-hidden="true">
                    {nouveaute.compte > 99 ? '99+' : nouveaute.compte}
                  </span>
                  <span>{nouveaute.libelle}</span>
                </li>
              ))}
            </ul>
            {/* La date de la dernière visite ferme la phrase : sans elle, « 2 nouveaux
                messages » ne dit pas depuis quand on compte. */}
            <p className="discret carte-dossier-depuis">
              depuis votre dernière visite
            </p>
          </div>
        ) : null}

        <Invisible>Ouvrir le dossier {titre}.</Invisible>
      </Link>
    </article>
  );
}
